import React from "react";
import { Box, Typography } from "@mui/material";
import { ACCENT, MONO, MEASURE } from "../data/studioStory";
import { DiagramFrame } from "./StudioDiagrams";
import commitHistory from "../data/commitHistory.json";

// Monthly commit counts, hand-rolled as inline SVG rather than a chart library.
// Three reasons, in order: the page is pre-rendered by react-snap, which fails
// the whole build on any third-party request; a bar chart of eighteen values is
// forty lines of SVG; and inline SVG lets the bars carry real text that a
// crawler and a screen reader both read without running JS.
//
// Data comes from scripts/build-commit-history.mjs (GitHub's own contribution
// figures, public + private). Re-run it to refresh — this is a committed
// snapshot, it does not update itself.

const VB_W = 680;
const VB_H = 250;
const PLOT_TOP = 22;
const PLOT_BOTTOM = 196;
const PLOT_H = PLOT_BOTTOM - PLOT_TOP;
const AXIS_L = 6;
const AXIS_R = 674;

const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function label(month) {
  const [y, m] = month.split("-");
  return { abbr: MONTH_ABBR[Number(m) - 1], year: y.slice(2) };
}

const fmt = (n) => n.toLocaleString("en-US");

function ordinal(n) {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  return `${n}${["th", "st", "nd", "rd"][n % 10] || "th"}`;
}

export default function CommitChart() {
  const months = commitHistory.months;
  const peak = Math.max(...months.map((m) => m.commits));
  const partial = months.find((m) => m.partial);
  const first = months[0];
  const lastFull = [...months].reverse().find((m) => !m.partial);

  const slot = (AXIS_R - AXIS_L) / months.length;
  const barW = Math.min(28, slot * 0.66);

  // Round the top gridline up to a clean number above the peak. The ladder is
  // deliberately all multiples of four, because the chart draws gridlines at
  // quarters — a ceiling of 12,000 gives 3/6/9/12k, where 12,500 would give
  // 3,125. Rounding to the next power of ten instead (the obvious version) put
  // a 10,212 peak under a 20,000 ceiling and wasted half the plot.
  const mag = Math.pow(10, Math.floor(Math.log10(peak)));
  const ceiling = ([1, 1.2, 1.6, 2, 2.4, 3, 4, 5, 6, 8].find((m) => peak <= m * mag) || 10) * mag;
  const y = (v) => PLOT_BOTTOM - (v / ceiling) * PLOT_H;

  const summary =
    `A bar chart of commits per month, from ${label(first.month).abbr} 20${label(first.month).year} to ` +
    `${label(months[months.length - 1].month).abbr} 20${label(months[months.length - 1].month).year}. ` +
    `It starts at ${fmt(first.commits)} commits a month and rises to ${fmt(lastFull.commits)} in ` +
    `${label(lastFull.month).abbr}${partial ? `, with the current month at ${fmt(partial.commits)} through the ${ordinal(partial.throughDay)}` : ""}.`;

  return (
    <Box>
      {/* Same frame as the studio diagrams, so the chart inherits the sideways
          scroll, the keyboard-reachable scroll region, and the mobile hint
          rather than shrinking its axis labels to ~5px on a phone. minWidth is
          set from the smallest label here (10.5) against the 12px floor. */}
      <DiagramFrame viewBox={`0 0 ${VB_W} ${VB_H}`} minWidth={780} label={summary}>
        {/* Numbers are tabular here, so the chart overrides the frame's sans
            stack with the mono one — set on a group so every child inherits. */}
        <g fontFamily={MONO}>
        {/* gridlines */}
        {[0, 0.25, 0.5, 0.75, 1].map((f) => (
          <g key={f}>
            <line
              x1={AXIS_L}
              y1={PLOT_BOTTOM - f * PLOT_H}
              x2={AXIS_R}
              y2={PLOT_BOTTOM - f * PLOT_H}
              stroke="currentColor"
              strokeOpacity={f === 0 ? 0.35 : 0.12}
            />
            {f > 0 && (
              <text
                x={AXIS_L + 2}
                y={PLOT_BOTTOM - f * PLOT_H - 4}
                // 11 user units, not 10: at the 780 minWidth this renders 12.6px
                // on a phone, where 10 rendered 11.5 and missed the 12px floor.
                fontSize="11"
                fill="currentColor"
                fillOpacity="0.5"
              >
                {fmt(Math.round(f * ceiling))}
              </text>
            )}
          </g>
        ))}

        {months.map((m, i) => {
          const cx = AXIS_L + slot * (i + 0.5);
          const top = y(m.commits);
          const lb = label(m.month);
          // The partial month is drawn hollow so it can't be read as a
          // finished bar sitting lower than the one before it.
          return (
            <g key={m.month}>
              <rect
                x={cx - barW / 2}
                y={top}
                width={barW}
                height={PLOT_BOTTOM - top}
                rx="2"
                fill={m.partial ? "none" : ACCENT}
                fillOpacity={m.partial ? 0 : 0.85}
                stroke={ACCENT}
                strokeOpacity={m.partial ? 0.9 : 0}
                strokeWidth={m.partial ? 1.25 : 0}
                strokeDasharray={m.partial ? "3 2" : undefined}
              />
              {/* value label on the milestone bars only — all 18 would be a wall of numbers */}
              {(m === first || m === lastFull || m.commits === peak) && (
                <text x={cx} y={top - 5} textAnchor="middle" fontSize="10.5" fill="currentColor" fillOpacity="0.85">
                  {fmt(m.commits)}
                </text>
              )}
              <text x={cx} y={PLOT_BOTTOM + 15} textAnchor="middle" fontSize="11" fill="currentColor" fillOpacity="0.6">
                {lb.abbr}
              </text>
              {(i === 0 || lb.abbr === "Jan") && (
                <text x={cx} y={PLOT_BOTTOM + 28} textAnchor="middle" fontSize="10.5" fill="currentColor" fillOpacity="0.45">
                  &rsquo;{lb.year}
                </text>
              )}
            </g>
          );
        })}

        {/* legend for the hollow bar */}
        {partial && (
          <g>
            <rect x={AXIS_L + 2} y={PLOT_BOTTOM + 36} width="18" height="10" rx="2" fill="none" stroke={ACCENT} strokeOpacity="0.9" strokeDasharray="3 2" />
            <text x={AXIS_L + 26} y={PLOT_BOTTOM + 45} fontSize="10.5" fill="currentColor" fillOpacity="0.7">
              {label(partial.month).abbr} is still running — {fmt(partial.commits)} through the {ordinal(partial.throughDay)}, not a full month
            </text>
          </g>
        )}
        </g>
      </DiagramFrame>

      <Typography sx={{ mt: 1.5, fontSize: "0.82rem", color: "text.secondary", lineHeight: 1.6, maxWidth: MEASURE }}>
        Commits per month across every repository I work in, public and private — GitHub&rsquo;s own
        count, so it can be checked. Volume isn&rsquo;t the achievement and a big number can be busy
        work; what changed is how much of a day&rsquo;s intent survives contact with the keyboard.
      </Typography>
    </Box>
  );
}
